import {useEffect, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {DatePicker, Form, Input, Select, Spin} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import DBModal from 'components/modal/modal';
import {TRANSLATE_MULTIPLE_FIELDS} from 'graphql/queries/investorUpdates';
import {GET_STOCK_CLOSURE} from 'graphql/queries/stocks';
import {
  CREATE_STOCK_CLOSURE,
  UPDATE_STOCK_CLOSURE,
} from 'graphql/mutations/stocks';
import {supportedLanguages} from 'utils/constants';
import styles from './exchanges.module.scss';

/**
 * Create or edit one closure window.
 *
 * Replaces a modal that carried three unrelated jobs behind a `type` prop —
 * create, edit, and a status change that shared none of the fields. Status is
 * a switch in the table now, so this only ever collects a closure.
 *
 * Translations are written per language rather than as a separate record: the
 * gateway stores the base language on the closure and every other language in
 * `multiLang`, which is what mobile reads.
 */

const languageLabel = value =>
  supportedLanguages.find(language => language.value === value)?.label ?? value;

export const ExchangeClosureModal = ({
  open,
  closure,
  exchangeId,
  exchangeCode,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [baseLanguage, setBaseLanguage] = useState('EN');
  // One entry per non-base language. Empty until something is written or
  // translated, so an untouched language is never saved as blank strings.
  const [translations, setTranslations] = useState([]);
  const [activeTab, setActiveTab] = useState('BASE');

  const isEdit = Boolean(closure?.id);

  const {data, loading} = useQuery(GET_STOCK_CLOSURE, {
    variables: {getStockExchangeClosureByIdId: closure?.id},
    skip: !closure?.id || !open,
    fetchPolicy: 'no-cache',
  });

  const [createClosure, {loading: creating}] = useMutation(
    CREATE_STOCK_CLOSURE,
    {errorPolicy: 'all'},
  );
  const [updateClosure, {loading: updating}] = useMutation(
    UPDATE_STOCK_CLOSURE,
    {errorPolicy: 'all'},
  );
  const [translateText, {loading: translating}] = useMutation(
    TRANSLATE_MULTIPLE_FIELDS,
    {errorPolicy: 'all'},
  );

  // Reset on every open so a previous closure never bleeds into the next one.
  useEffect(() => {
    if (!open) return;
    setActiveTab('BASE');
    if (!closure?.id) {
      form.resetFields();
      setBaseLanguage('EN');
      setTranslations([]);
    }
  }, [open, closure?.id]);

  useEffect(() => {
    const saved = data?.getStockExchangeClosureById;
    if (!saved || saved.__typename === 'Error') return;
    setBaseLanguage(saved.language || 'EN');
    form.setFieldsValue({
      language: saved.language || 'EN',
      title: saved.title,
      message: saved.closureMessage,
      date: saved.date ? moment(saved.date, 'YYYY-MM-DD HH:mm:ss') : null,
    });
    setTranslations(
      (saved.multiLang ?? []).map(entry => ({
        language: entry.language,
        title: entry.title,
        message: entry.closureMessage,
      })),
    );
  }, [data]);

  const otherLanguages = supportedLanguages.filter(
    language => language.value !== baseLanguage,
  );

  const translationFor = language =>
    translations.find(entry => entry.language === language);

  const writeTranslation = (language, changes) =>
    setTranslations(previous => {
      const existing = previous.find(entry => entry.language === language);
      if (!existing) return [...previous, {language, ...changes}];
      return previous.map(entry =>
        entry.language === language ? {...entry, ...changes} : entry,
      );
    });

  const handleTranslate = async language => {
    const title = form.getFieldValue('title');
    const message = form.getFieldValue('message');
    if (!title || !message)
      return toast.error('Write the title and message first');
    const {data: payload} = await translateText({
      variables: {
        translations: [
          {sourceLang: baseLanguage, sourceText: title, targetLang: language},
          {sourceLang: baseLanguage, sourceText: message, targetLang: language},
        ],
        treatment: 'html',
      },
    });
    const out = payload?.translateText?.translations;
    if (!out?.length) return toast.error('Translation failed');
    writeTranslation(language, {title: out[0], message: out[1]});
    toast.success(`Translated to ${languageLabel(language)}`);
  };

  const handleFinish = values => {
    const input = {
      title: values.title,
      closureMessage: values.message,
      date: values.date,
      language: baseLanguage,
      stockExchange: exchangeId,
      // Only languages that actually carry both fields. Sending a half-filled
      // entry publishes an empty title to anyone reading in that language.
      multiLang: translations
        .filter(entry => entry.title && entry.message)
        .map(entry => ({
          language: entry.language,
          title: entry.title,
          closureMessage: entry.message,
        })),
    };

    const run = isEdit
      ? updateClosure({
          variables: {updateStockExchangeClosureId: closure.id, input},
        })
      : createClosure({variables: {input}});

    run
      .then(({data: payload}) => {
        const outcome =
          payload?.updateStockExchangeClosure ??
          payload?.createStockExchangeClosure;
        if (outcome?.__typename === 'Error')
          return toast.error(outcome.message);
        toast.success(isEdit ? 'Closure updated' : 'Closure created');
        onSaved();
      })
      .catch(error => toast.error(error?.message));
  };

  const saving = creating || updating;

  return (
    <DBModal
      isOpen={open}
      width={720}
      handleClose={onClose}
      content={
        loading ? (
          <div className={styles.centered}>
            <Spin />
          </div>
        ) : (
          <div className={styles.closureModal}>
            <h2>{isEdit ? 'Edit closure window' : 'New closure window'}</h2>
            <p className={styles.closureModalSub}>
              {exchangeCode
                ? `${exchangeCode} will be shut on this date.`
                : 'The exchange will be shut on this date.'}{' '}
              Investors see the title and message when they try to place an
              order.
            </p>

            <Form form={form} layout="vertical" onFinish={handleFinish}>
              <div className={styles.closureRow}>
                <Form.Item
                  label="Date and time"
                  name="date"
                  rules={[{required: true, message: 'Pick a date'}]}>
                  <DatePicker
                    className={styles.fullWidth}
                    format="YYYY-MM-DD HH:mm"
                    showTime={{defaultValue: moment('00:00', 'HH:mm')}}
                  />
                </Form.Item>
                <Form.Item label="Written in" name="language" initialValue="EN">
                  <Select
                    value={baseLanguage}
                    onChange={setBaseLanguage}
                    options={supportedLanguages}
                  />
                </Form.Item>
              </div>

              {/* Plain buttons rather than antd Tabs: the translated fields
                  are not Form.Items, and Tabs unmount their panes, which threw
                  away half-written translations on every switch. */}
              <div className={styles.langTabs}>
                <button
                  type="button"
                  className={activeTab === 'BASE' ? styles.langTabOn : ''}
                  onClick={() => setActiveTab('BASE')}>
                  {languageLabel(baseLanguage)}
                  <span className={styles.langTabNote}>original</span>
                </button>
                {otherLanguages.map(language => {
                  const entry = translationFor(language.value);
                  const done = Boolean(entry?.title && entry?.message);
                  return (
                    <button
                      key={language.value}
                      type="button"
                      className={
                        activeTab === language.value ? styles.langTabOn : ''
                      }
                      onClick={() => setActiveTab(language.value)}>
                      {language.label}
                      <span className={styles.langTabNote}>
                        {done ? 'written' : 'empty'}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Both panes stay mounted; the inactive one is hidden so its
                  values survive a switch. */}
              <div style={{display: activeTab === 'BASE' ? 'block' : 'none'}}>
                <Form.Item
                  label="Title"
                  name="title"
                  rules={[{required: true, message: 'Title is required'}]}>
                  <Input placeholder="Public holiday" />
                </Form.Item>
                <Form.Item
                  label="Message to investors"
                  name="message"
                  rules={[{required: true, message: 'Message is required'}]}>
                  <Input.TextArea
                    rows={4}
                    placeholder="The market is closed today. Your order will be placed when it reopens."
                  />
                </Form.Item>
              </div>

              {otherLanguages.map(language => {
                const entry = translationFor(language.value) ?? {};
                return (
                  <div
                    key={language.value}
                    style={{
                      display: activeTab === language.value ? 'block' : 'none',
                    }}>
                    <div className={styles.translateBar}>
                      <span>
                        Leave this blank and investors reading in{' '}
                        {language.label} see the {languageLabel(baseLanguage)}{' '}
                        text.
                      </span>
                      <Button
                        type="secondary"
                        disabled={translating}
                        label={
                          translating
                            ? 'Translating…'
                            : `Translate from ${languageLabel(baseLanguage)}`
                        }
                        onClick={() => handleTranslate(language.value)}
                      />
                    </div>
                    <Form.Item label={`Title (${language.label})`}>
                      <Input
                        value={entry.title}
                        placeholder="Jour férié"
                        onChange={event =>
                          writeTranslation(language.value, {
                            title: event.target.value,
                          })
                        }
                      />
                    </Form.Item>
                    <Form.Item label={`Message (${language.label})`}>
                      <Input.TextArea
                        rows={4}
                        value={entry.message}
                        onChange={event =>
                          writeTranslation(language.value, {
                            message: event.target.value,
                          })
                        }
                      />
                    </Form.Item>
                  </div>
                );
              })}

              <div className={styles.closureModalActions}>
                <Button type="secondary" label="Cancel" onClick={onClose} />
                <Button
                  disabled={saving}
                  label={
                    saving
                      ? 'Saving…'
                      : isEdit
                      ? 'Save changes'
                      : 'Create closure'
                  }
                  onClick={() => form.submit()}
                />
              </div>
            </Form>
          </div>
        )
      }
    />
  );
};

export default ExchangeClosureModal;
